export type OfflineLogType = 'offline' | 'online' | 'save-queued' | 'save-ok' | 'submit-queued' | 'submit-ok';

export interface OfflineLogEvent {
  type: OfflineLogType;
  at: string;
  seq: number;
}

export interface ConnectionPeriod {
  from: string;
  to: string | null;
}

export interface AttemptDraft {
  answers: Record<string, unknown>;
  seq: number;
  deviceId: string;
  clientSubmissionId: string | null;
  pendingSubmit: boolean;
  /**
   * Unconfirmed full answer map. Progress always posts every answer, so a
   * questionId list would not shrink the payload; the flag is enough for the
   * save indicator and the retry.
   */
  dirty: boolean;
  deadlineAt: string | null;
  graceUntil: string | null;
  queuedAt: string | null;
  /** offline/online are paired into periods on submit. save-queued and save-ok stay events. */
  offlineLog: OfflineLogEvent[];
  serverOffsetMs?: number | null;
  savedAt?: string | null;
  progressUrl: string;
  submitUrl: string;
  fileUrl: string;
  updatedAt: string;
}

export interface AttemptStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

export function attemptStorageKey(userId: string, attemptId: string): string {
  return `lms:attempt:v1:${userId}:${attemptId}`;
}

/** Stable id already stored on devices. `lms:device-id` and `lms:attempt:device` are copied here once. */
export const DEVICE_KEY = 'lms:attempt:v1:device';
const LEGACY_DEVICE_KEYS = ['lms:attempt:device', 'lms:device-id'];
export const OFFLINE_LOG_LIMIT = 200;
export const PENDING_LOGOUT_MESSAGE =
  'Tienes un envío pendiente; si cierras sesión se enviará cuando vuelvas a entrar';

export function appendOfflineLog(
  log: OfflineLogEvent[] | undefined,
  event: OfflineLogEvent,
): OfflineLogEvent[] {
  return [...(log ?? []), event].slice(-OFFLINE_LOG_LIMIT);
}

/** Pairs offline→online. An open offline stretch stays open (`to: null`) until the next online. */
export function connectionPeriods(log: OfflineLogEvent[] | undefined, closedAt?: string | null): ConnectionPeriod[] {
  const periods: ConnectionPeriod[] = [];
  let open: string | null = null;
  for (const event of log ?? []) {
    if (event.type === 'offline' && !open) open = event.at;
    if (event.type === 'online' && open) {
      periods.push({ from: open, to: event.at });
      open = null;
    }
  }
  if (open) periods.push({ from: open, to: closedAt ?? null });
  return periods;
}

export function withProgressAck(
  draft: AttemptDraft,
  body: {
    acceptedSeq?: number | null;
    deadlineAt?: string | null;
    graceUntil?: string | null;
    serverNow?: string | null;
  },
  at = new Date().toISOString(),
  serverOffsetMs?: number | null,
): AttemptDraft {
  const accepted = body.acceptedSeq ?? null;
  const dirty = accepted == null ? draft.dirty : accepted < draft.seq;
  return {
    ...draft,
    dirty,
    savedAt: !dirty && accepted != null ? at : (draft.savedAt ?? null),
    serverOffsetMs: body.serverNow && serverOffsetMs != null ? serverOffsetMs : (draft.serverOffsetMs ?? null),
    deadlineAt: body.deadlineAt ?? draft.deadlineAt,
    graceUntil: body.graceUntil ?? draft.graceUntil,
    offlineLog: appendOfflineLog(draft.offlineLog, { type: 'save-ok', at, seq: accepted ?? draft.seq }),
  };
}

export function logoutNotice(drafts: Array<{ pendingSubmit: boolean }>, online: boolean): string | null {
  if (online || !drafts.some((draft) => draft.pendingSubmit)) return null;
  return PENDING_LOGOUT_MESSAGE;
}

export function readDeviceId(storage: Pick<AttemptStorage, 'getItem' | 'setItem' | 'removeItem'>): string {
  const existing = storage.getItem(DEVICE_KEY);
  if (existing) return existing;
  for (const legacyKey of LEGACY_DEVICE_KEYS) {
    const legacy = storage.getItem(legacyKey);
    if (!legacy) continue;
    storage.setItem(DEVICE_KEY, legacy);
    storage.removeItem(legacyKey);
    return legacy;
  }
  const created = crypto.randomUUID();
  storage.setItem(DEVICE_KEY, created);
  return created;
}

export function createAttemptOfflineStore(storage: AttemptStorage) {
  function read(key: string): AttemptDraft | null {
    const raw = storage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AttemptDraft;
      if (!parsed || typeof parsed !== 'object' || !parsed.answers) return null;
      return {
        ...parsed,
        dirty: Boolean(parsed.dirty),
        deadlineAt: parsed.deadlineAt ?? null,
        graceUntil: parsed.graceUntil ?? null,
        queuedAt: parsed.queuedAt ?? null,
        offlineLog: Array.isArray(parsed.offlineLog) ? parsed.offlineLog.slice(-OFFLINE_LOG_LIMIT) : [],
        serverOffsetMs: typeof parsed.serverOffsetMs === 'number' ? parsed.serverOffsetMs : null,
        savedAt: parsed.savedAt ?? null,
        pendingSubmit: Boolean(parsed.pendingSubmit),
      };
    } catch {
      return null;
    }
  }

  return {
    load(userId: string, attemptId: string): AttemptDraft | null {
      return read(attemptStorageKey(userId, attemptId));
    },
    save(userId: string, attemptId: string, draft: AttemptDraft) {
      storage.setItem(attemptStorageKey(userId, attemptId), JSON.stringify(draft));
    },
    clear(userId: string, attemptId: string) {
      storage.removeItem(attemptStorageKey(userId, attemptId));
    },
    listUser(userId: string): Array<{ attemptId: string; draft: AttemptDraft }> {
      const prefix = `lms:attempt:v1:${userId}:`;
      const rows: Array<{ attemptId: string; draft: AttemptDraft }> = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key?.startsWith(prefix)) continue;
        const draft = read(key);
        if (draft) rows.push({ attemptId: key.slice(prefix.length), draft });
      }
      return rows;
    },
    clearUser(userId: string) {
      const prefix = `lms:attempt:v1:${userId}:`;
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) keys.push(key);
      }
      for (const key of keys) storage.removeItem(key);
    },
  };
}

export function browserAttemptStore() {
  return createAttemptOfflineStore(localStorage);
}
